import React, {useEffect, useRef, useState} from 'react';
import {Card, Typography, TextField, Box} from '@mui/material';
import './IssueCodeType.scss';
import ArrTooltipWrapper from '../../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {t} from 'i18next';
import {ReactComponent as InfoIcon} from './../../../../../../assets/images/info-icon.svg';
import {ReactComponent as ResetIcon} from './../../../../../../assets/images/icon-reset.svg';
import ArrButton from '../../../../../common/arr-button/ArrButton';
import {ReactComponent as AddIcon} from '../../../../../../assets/images/add.svg';
import {ReactComponent as WarningIcon} from '../../../../../../assets/images/warning.svg';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ArrIconButton from '../../../../../common/arr-icon-button/ArrIconButton';
import {ReactComponent as DeleteIcon} from './../../../../../../assets/images/delete.svg';
import ArrToastNotification from '../../../../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../../../../shared/constants';
import ArrModal from '../../../../../common/arr-modal/ArrModal';
import IssueCodingTypeHeader, {
  CRITERIA,
} from '../issue-coding-type-header/IssueCodingTypeHeader';
import {
  MAX_HEIGHT,
  MIN_HEIGHT,
  NAME_REGEX,
  PROTOCOL_INTELLIGENCE_FIELDS,
} from '../../../constant/AiReviewConstants';
import ArrTextArea from '../../../../../common/arr-text-area/ArrTextArea';
import TextAreaComponent from '../../../common/TextAreaComponent/TextAreaComponent';
import ArrBanner from '../../../../../common/arr-banner/ArrBanner';
import {ReactComponent as SuccessIcon} from '../../../../../../../src/assets/images/success-icon.svg';
import {IssueCode} from '../../../../../../api/types/aiReview.type';
import {useDispatch, useSelector} from 'react-redux';
import {
  setEditMode,
  setFormData,
} from '../../../../../../redux/slice/aiReviewSlice';
import {RootState} from '../../../../../../redux/store';
import {v4 as uuidv4} from 'uuid';
import CreateJobsFieldsWrapper from '../../../common/case-context-container/CreateJobsFieldsWrapper';

interface IssueCodeProps {
  hasSpecialCharError: boolean;

  setIsValid?: (isValid: boolean) => void;
  isProtocolDisabled: boolean;
  handleUseSavedProtocol: (value?: string) => void;
  clearAllContents: () => void;
}

export interface IssueCodeTypeJobRef {
  validateAll: () => boolean;
  // hasSpecialCharError: () => boolean;
  isNextDisabled: () => boolean;
}
const maxLength = 10000;
const nameRegex = NAME_REGEX;

const IssueCodeTypeJob = React.forwardRef<IssueCodeTypeJobRef, IssueCodeProps>(
  (
    {
      setIsValid,
      isProtocolDisabled,
      hasSpecialCharError,
      handleUseSavedProtocol,
      clearAllContents,
    },
    ref
  ) => {
    const [showWarning, setShowWarning] = useState(false);
    const [deleteModal, setDeleteModal] = useState<{
      open: boolean;
      id: string | null;
    }>({open: false, id: null});
    const [touchedTile, setTouchedTile] = useState<Record<string, boolean>>({});

    const issueCodeContainerRef = useRef<HTMLDivElement | null>(null);
    const newlyAddedIdRef = useRef<string | null>(null);

    const formData = useSelector((state: RootState) => state.aiReview.formData);
    const editMode = useSelector((state: RootState) => state.aiReview.editMode);

    const defineCriteria = useSelector(
      (state: RootState) => state.aiReview.defineCriteria
    );
    const dispatch = useDispatch();

    // --- Independent resize logic for each card ---
    // Track individual heights
    const [issueCodeHeight, setIssueCodeHeight] = useState(MAX_HEIGHT);

    // For issue code card
    const issueCodeDrag = useRef({
      dragging: false,
      startY: 0,
      startHeight: MAX_HEIGHT,
    });

    const handleTileBlur = (id: string) => {
      setTouchedTile((prev) => ({...prev, [id]: true}));
    };

    const handleIssueCodeMouseDown = (e: React.PointerEvent<HTMLElement>) => {
      issueCodeDrag.current.dragging = true;
      issueCodeDrag.current.startY = e.clientY;
      issueCodeDrag.current.startHeight =
        issueCodeContainerRef.current?.offsetHeight || MIN_HEIGHT;
      document.body.style.userSelect = 'none';
      document.addEventListener('pointermove', handleIssueCodeMouseMove);
      document.addEventListener('pointerup', handleIssueCodeMouseUp);
    };

    const handleIssueCodeMouseMove = (e: PointerEvent) => {
      if (!issueCodeDrag.current.dragging) return;
      const delta = e.clientY - issueCodeDrag.current.startY;
      let newHeight = issueCodeDrag.current.startHeight + delta;
      newHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, newHeight));
      setIssueCodeHeight(newHeight);
    };

    const handleIssueCodeMouseUp = () => {
      issueCodeDrag.current.dragging = false;
      document.body.style.userSelect = '';
      document.removeEventListener('pointermove', handleIssueCodeMouseMove);
      document.removeEventListener('pointerup', handleIssueCodeMouseUp);
    };

    const handleChange = (value: string) => {
      if (defineCriteria === CRITERIA.SAVED)
        dispatch(setEditMode({caseContext: true}));
      if (value.length <= maxLength) {
        dispatch(
          setFormData({
            contextAndCriteria: {
              ...formData.contextAndCriteria,
              context: value,
            },
          })
        );
      }
    };

    const validateIssueCode = (code: IssueCode) => {
      return (
        code.code.trim() !== '' &&
        code.question.trim() !== '' &&
        !hasSpecialCharError
      );
    };

    const isAddDisabled =
      formData.contextAndCriteria.issueCodes.length > 0 &&
      !validateIssueCode(
        formData.contextAndCriteria.issueCodes[
          formData.contextAndCriteria.issueCodes.length - 1
        ]
      );

    const handleAddIssueCode = () => {
      if (isAddDisabled) return;
      const newId = uuidv4();
      newlyAddedIdRef.current = newId;
      const newIssue: IssueCode = {
        id: newId,
        code: '',
        question: '',
      };

      dispatch(
        setFormData({
          contextAndCriteria: {
            ...formData.contextAndCriteria,
            issueCodes: [...formData.contextAndCriteria.issueCodes, newIssue],
          },
        })
      );
    };

    const handleDeleteIssueCode = (id: string) => {
      setDeleteModal({open: true, id});
    };

    const confirmDeleteIssueCode = () => {
      if (deleteModal.id === null) return;
      const updated = formData.contextAndCriteria.issueCodes.filter(
        (item) => item.id !== deleteModal.id
      );
      dispatch(
        setFormData({
          contextAndCriteria: {
            ...formData.contextAndCriteria,
            issueCodes: updated,
          },
        })
      );
      setDeleteModal({open: false, id: null});
    };

    const hasItemSpecialCharError = (code: string) =>
      code.trim() !== '' && !nameRegex.test(code);
    const handleIssueCodeChange = (
      id: string,
      field: keyof IssueCode,
      value: string
    ) => {
      if (defineCriteria === CRITERIA.SAVED)
        dispatch(setEditMode({issueCode: true}));
      dispatch(
        setFormData({
          contextAndCriteria: {
            ...formData.contextAndCriteria,
            issueCodes: formData.contextAndCriteria.issueCodes.map((tile) =>
              tile.id === id ? {...tile, [field]: value} : tile
            ),
          },
        })
      );
    };

    //sets missing ids
    useEffect(() => {
      const issueCodes = formData.contextAndCriteria.issueCodes;
      const needsIds = issueCodes.some((code) => !code.id);
      if (!needsIds) return;

      dispatch(
        setFormData({
          contextAndCriteria: {
            ...formData.contextAndCriteria,
            issueCodes: issueCodes.map((code) => ({
              ...code,
              id: code.id ?? uuidv4(),
            })),
          },
        })
      );
    }, []);
    useEffect(() => {
      const {issueCodes, context} = formData.contextAndCriteria;
      const isValid =
        issueCodes.length === 0
          ? context.trim() !== ''
          : Array.isArray(formData?.contextAndCriteria?.issueCodes) &&
            issueCodes.every(
              (c) => c.code.trim() !== '' && c.question.trim() !== ''
            );
      setIsValid?.(isValid);
    }, [formData.contextAndCriteria]);

    React.useImperativeHandle(ref, () => ({
      validateAll: () => {
        const issueCodes = formData.contextAndCriteria.issueCodes;
        const hasIncomplete = issueCodes.some(
          (code) =>
            code.code.trim() === '' ||
            code.question.trim() === '' ||
            !nameRegex.test(code.code)
        );

        if (hasIncomplete) {
          const allTouched = issueCodes.reduce(
            (acc, code) => ({...acc, [code.id!]: true}),
            {}
          );
          setTouchedTile(allTouched);
          setShowWarning(true);
        }

        return !hasIncomplete;
      },

      isNextDisabled: () => {
        if (formData.contextAndCriteria.issueCodes.length === 0) {
          return formData.contextAndCriteria.context.trim() === '';
        }

        // If issue codes exist → all must be valid
        return (
          Array.isArray(formData?.contextAndCriteria?.issueCodes) &&
          formData.contextAndCriteria.issueCodes.some((code) => {
            return code.code.trim() === '' || code.question.trim() === '';
          })
        );
      },
    }));

    return (
      <>
        {showWarning && (
          <ArrToastNotification
            message={t('ai.review.newJobDetail.incompleteIssueCodesDetected')}
            onClose={() => setShowWarning(false)}
            status={[ToastType.Warning]}
            title={t('ai.review.newJobDetail.emptyIssueCodeTitle')}
          />
        )}
        <Box className="main-context-criteria">
          <IssueCodingTypeHeader
            isProtocolDisabled={isProtocolDisabled}
            handleUseSavedProtocol={handleUseSavedProtocol}
            clearAllContents={clearAllContents}
          />
          <Box className="cards-wrapper">
            {/* Case Context */}
            <TextAreaComponent
              resizable
              displayHandle
              placeholder={t(
                'ai.review.contextAndCriteria.caseContextPlaceholder'
              )}
              label={t('ai.review.contextAndCriteria.caseContext')}
              data-testid="contextAndCriteria-caseContext-input"
              value={formData.contextAndCriteria.context}
              maxLength={10000}
              helperText={`${formData.contextAndCriteria.context.length.toLocaleString()} / 10,000`}
              onChange={handleChange}
              displayOptionalLabel={false}
              displayArrTooltip={true}
              toolTipTitle={t(
                'ai.review.contextAndCriteria.caseContextToolTipTitle'
              )}
              toolTipContent={t(
                'ai.review.contextAndCriteria.caseContextToolTipContent'
              )}
              MIN_HEIGHT={MIN_HEIGHT}
              MAX_HEIGHT={MAX_HEIGHT}
              IsDefaultValueMinHeight={window.innerWidth <= 800}
              enableBanner={
                formData.contextAndCriteria.context !== '' &&
                defineCriteria === CRITERIA.SAVED
              }
              isEditMode={
                defineCriteria === CRITERIA.SAVED && editMode.caseContext
              }
              bannerContent={
                editMode.caseContext
                  ? t('ai.review.contextAndCriteria.editBannerContent')
                  : t(
                      'ai.review.contextAndCriteria.caseContextSavedBannerContent'
                    )
              }
              bannerAction={() => {
                dispatch(setEditMode({caseContext: false}));

                handleUseSavedProtocol(
                  PROTOCOL_INTELLIGENCE_FIELDS.caseContext
                );
              }}
              maxRows={20}
            />

            {/* Issue Codes */}

            <CreateJobsFieldsWrapper
              resizable
              displayHandle
              MAX_HEIGHT={MAX_HEIGHT}
              MIN_HEIGHT={MIN_HEIGHT}
              className="issue-codes-section"
              header={
                <Box className="header-content">
                  <Typography
                    className="typo-text subhead4_size title"
                    tabIndex={0}
                    data-testid={`issue-code-type-job-issue-codes`}
                  >
                    {t('ai.review.newJobDetail.issueCodes')}
                  </Typography>

                  <ArrTooltipWrapper
                    title={t('ai.review.newJobDetail.issueCodesTooltipTitle')}
                    aria-label={t('ai.review.newJobDetail.issueCodesTooltip')}
                    trigger={
                      <InfoIcon
                        className="typo-text subhead4_size info-icon"
                        fontSize="medium"
                        aria-label="information"
                        data-testid="issueCodes-tooltip-info"
                        tabIndex={0}
                      />
                    }
                  >
                    <Box
                      aria-label={t('ai.review.newJobDetail.issueCodesTooltip')}
                      tabIndex={0}
                    >
                      {t('ai.review.newJobDetail.issueCodesTooltip')}
                    </Box>
                  </ArrTooltipWrapper>

                  <Box className="issue-btn-wrapper">
                    <ArrButton
                      variant="primary"
                      size="small"
                      onClick={handleAddIssueCode}
                      disabled={isAddDisabled}
                      aria-label={t('ai.review.newJobDetail.ariaIssueCode')}
                      dataTestId="issue-code-type-job-add-issue-code-button"
                      startIcon={
                        <AddIcon
                          className="add-icon"
                          aria-hidden="true"
                          focusable="false"
                        />
                      }
                    >
                      {t('ai.review.newJobDetail.addIssueCode')}
                    </ArrButton>
                  </Box>
                </Box>
              }
            >
              {defineCriteria === CRITERIA.SAVED &&
                formData.contextAndCriteria.issueCodes.length > 0 && (
                  <ArrBanner
                    variant={editMode.issueCode ? 'info' : 'success'}
                    className="banner-class"
                    dataTestId="issue-code-type-banner"
                    bannerContent={
                      editMode.issueCode
                        ? t('ai.review.contextAndCriteria.editBannerContent')
                        : t(
                            'ai.review.contextAndCriteria.issueCodesSavedBannerContent',
                            {
                              value:
                                formData.contextAndCriteria.issueCodes.length,
                            }
                          )
                    }
                    type="contextual"
                    startIcon={
                      editMode.issueCode ? <InfoIcon /> : <SuccessIcon />
                    }
                    actions={
                      editMode.issueCode
                        ? [
                            {
                              label: 'Reset',
                              startIcon: <ResetIcon />,
                              dataTestId: 'reset-action',
                              onClick: () => {
                                dispatch(setEditMode({issueCode: false}));

                                handleUseSavedProtocol(
                                  PROTOCOL_INTELLIGENCE_FIELDS.issueCodes
                                );
                              },
                            },
                          ]
                        : []
                    }
                  />
                )}

              <Box className="issue-code-card-content">
                {Array.isArray(formData?.contextAndCriteria?.issueCodes) &&
                  formData?.contextAndCriteria?.issueCodes?.map(
                    (item, index) => {
                      const currentCharCount = item.code.length;
                      const charCounterId = `issue-code-name-counter-${item.id}`;
                      const errorId = `issue-code-name-error-${item.id}`;
                      const hasError =
                        touchedTile[item.id!] && item.code.trim() === '';

                      const textAreacharCounterId = `issue-code-description-counter-${item.id}`;
                      const textareaErrorId = `issue-code-question-error-${item.id}`;
                      const textareaHasError =
                        touchedTile[item.id!] && !item.question.trim();

                      return (
                        <Box
                          className={`issue-code-item  
                            ${newlyAddedIdRef.current === item.id ? 'issue-code-new-tile' : ''}
                              ${
                                touchedTile[item.id!] &&
                                item.code.trim() === '' &&
                                item.question.trim() === ''
                                  ? 'issue-code-tile--error'
                                  : ''
                              }`}
                          key={item.id}
                          onBlur={(e) => {
                            if (!e.currentTarget.contains(e.relatedTarget)) {
                              newlyAddedIdRef.current = null;
                              handleTileBlur(item.id!);
                            }
                          }}
                        >
                          {/* Header */}
                          <Box className="issue-code-header">
                            <TextField
                              placeholder={t(
                                'ai.review.newJobDetail.addIssueCodePlaceholder'
                              )}
                              variant="outlined"
                              size="small"
                              autoFocus
                              className="typo-text body3_size issue-code-input"
                              value={item.code}
                              error={
                                touchedTile[item.id!] &&
                                (hasItemSpecialCharError(item.code) ||
                                  (item.code.trim() === '' &&
                                    item.question.trim() !== ''))
                              }
                              helperText={
                                touchedTile[item.id!] &&
                                hasItemSpecialCharError(item.code)
                                  ? t('ai.review.newJobDetail.splCharError')
                                  : touchedTile[item.id!] &&
                                      item.code.trim() === '' &&
                                      item.question.trim() !== ''
                                    ? t(
                                        'ai.review.newJobDetail.issueCodeNameRequired'
                                      )
                                    : `${item.code.length} / 50`
                              }
                              data-testid="add-issue-code-text-field"
                              aria-invalid={hasError}
                              aria-describedby={`${charCounterId} ${hasError ? errorId : ''}`}
                              aria-label={t(
                                'ai.review.newJobDetail.addIssueCodeAriaLabel',
                                {
                                  issueCodeId: `section-${index + 1}`,
                                }
                              )}
                              FormHelperTextProps={{
                                className: hasItemSpecialCharError(item.code)
                                  ? 'typo-text body4_size'
                                  : 'typo-text body4_size character-count-text',
                              }}
                              onChange={(e) =>
                                handleIssueCodeChange(
                                  item.id!,
                                  'code',
                                  e.target.value
                                )
                              }
                              inputProps={{
                                maxLength: 50, // This strictly restricts the user from typing more than 50 chars
                              }}
                              InputProps={{
                                endAdornment: hasItemSpecialCharError(
                                  item.code
                                ) ? (
                                  <ErrorOutlineIcon
                                    aria-hidden="true"
                                    className="textfield-error-icon"
                                  />
                                ) : null,
                              }}
                            />
                            <Typography
                              id={hasError ? errorId : charCounterId}
                              className="sr-only"
                              data-testid="sr-only-add-issue-code-text-field"
                            >
                              {hasError
                                ? t(
                                    'ai.review.newJobDetail.issueCodeNameRequired'
                                  )
                                : `${currentCharCount} of {50} characters used`}
                            </Typography>

                            {touchedTile[item.id!] &&
                              item.code.trim() === '' &&
                              item.question.trim() === '' && (
                                <Box
                                  className="warning-icon-wrapper"
                                  tabIndex={-1}
                                >
                                  <ArrIconButton
                                    testId={`arr-warning-icon-${item.id}`}
                                    tabIndex={-1}
                                    icon={<WarningIcon />}
                                    ariaLabel={'warning-icon'}
                                    className="warning-icon"
                                  />
                                </Box>
                              )}
                            <Box className="delete-icon-wrapper">
                              <ArrIconButton
                                testId={`arr-delete-icon-${item.id}`}
                                onClick={() => handleDeleteIssueCode(item.id!)}
                                icon={<DeleteIcon />}
                                ariaLabel={t(
                                  'ai.review.newJobDetail.deleteIssueCodeAriaLabel',
                                  {
                                    issueCodeId: `section-${index + 1}`,
                                  }
                                )}
                                shape="none"
                              />
                            </Box>
                          </Box>
                          <Box>
                            <ArrTextArea
                              dataTestId={`text-area-${index}`}
                              tabIndex={0}
                              placeholder={t(
                                'ai.review.newJobDetail.addIssueCodeQuestionPlaceholder'
                              )}
                              maxLength={1000}
                              value={item.question}
                              error={
                                touchedTile[item.id!] &&
                                item.code.trim() !== '' &&
                                item.question.trim() === ''
                              }
                              helperText={
                                touchedTile[item.id!] &&
                                item.code.trim() !== '' &&
                                item.question.trim() === ''
                                  ? t(
                                      'ai.review.reviewAndApprove.issueCodeQtnFieldError'
                                    )
                                  : `${item.question.length} / 1,000`
                              }
                              className={`typo-text body3_size issue-code-textarea  ${
                                touchedTile[item.id!] &&
                                item.code.trim() !== '' &&
                                item.question.trim() === ''
                                  ? 'error'
                                  : ''
                              }`}
                              aria-invalid={textareaHasError}
                              aria-describedby={`${currentCharCount} ${textareaHasError ? textareaErrorId : ''}`}
                              aria-label={t(
                                'ai.review.newJobDetail.addIssueCodeQuestionPlaceholderAriaLabel',
                                {
                                  issueCodeId: `section-${index + 1}`,
                                }
                              )}
                              onChange={(value) =>
                                handleIssueCodeChange(
                                  item.id!,
                                  'question',
                                  value
                                )
                              }
                            />
                            <Typography
                              id={textAreacharCounterId}
                              className="sr-only"
                              role="status"
                              aria-live="polite"
                              aria-atomic="true"
                              data-testid={`sr-only-counter-text-area-${index}`}
                            >
                              {item.question.length}
                              {t('ai.review.newJobDetail.ofCharUsed')}
                            </Typography>

                            <Typography
                              id={textareaErrorId}
                              className="sr-only"
                              role="status"
                              aria-live="polite"
                              aria-atomic="true"
                              data-testid={`sr-only-error-text-area-${index}`}
                            >
                              {t(
                                'ai.review.reviewAndApprove.issueCodeQtnFieldError'
                              )}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    }
                  )}
              </Box>
            </CreateJobsFieldsWrapper>
          </Box>
        </Box>
        {/* Delete confirmation modal */}
        <ArrModal
          showModal={deleteModal.open}
          showClose={false}
          onClose={() => setDeleteModal({open: false, id: null})}
          title={t('ai.review.newJobDetail.deleteIssueCode')}
          aria-label={t('ai.review.newJobDetail.deleteIssueCode')}
          description={
            <Typography
              className="typo-text body2_size"
              tabIndex={0}
              data-testid={`issue-code-type-job-delete-issue-code-confirmation`}
            >
              {t('ai.review.newJobDetail.deleteIssueCodeConfirmation')}
            </Typography>
          }
          dataTestId="issue-code-type-job-modal"
          buttons={[
            {
              id: '1',
              label: t('common.cancel'),
              onClick: () => setDeleteModal({open: false, id: null}),
              variant: 'secondary',
              dataTestId: 'isctj-cancel-btn',
            },
            {
              id: '2',
              label: t('Delete'),
              onClick: confirmDeleteIssueCode,
              variant: 'danger',
              dataTestId: 'isctj-cancel-btn',
            },
          ]}
        />
      </>
    );
  }
);

export default IssueCodeTypeJob;
